import { useEffect, useRef, useState } from "react";

export interface BunnyVideoProps {
  src: string;
  poster?: string;
  title: string;
  /** Optional accent colour hex (e.g. "#3b82f6"). Used for the fallback gradient
   *  when the poster image fails to load. */
  accentColor?: string;
  /** Optional lesson number to show in the fallback. */
  lessonOrder?: string | number;
}

/**
 * Bunny CDN video player with practice-grade extras:
 *
 * 1. **Always-on floating dock** (desktop only) — once the inline frame leaves
 *    the viewport, the player snaps to a corner mini-player and stays there
 *    until the user scrolls back.
 *
 *    IMPORTANT: the <video> element is NEVER unmounted during this transition.
 *    Instead, its CSS class switches between `position:absolute` (inline) and
 *    `position:fixed` (corner dock). This preserves currentTime and play state
 *    with zero interruption — no pause, no re-seek, no autoplay fight.
 *
 *    On mobile (≤640 px) floating is disabled entirely: the video scrolls off-
 *    screen naturally. iOS Safari may pause a fully off-screen video; that is
 *    an OS-level limit, not our bug.
 *
 * 2. **One-click musical A-B loop** with student-friendly UX:
 *    - "Loop last 5s / 10s / 30s" buttons set A=now-N, B=now and immediately loop
 *    - "Set A" / "Set B" / "Loop" still available for precise control
 *    - "⇤ A" jumps to A
 *    - Speed: 0.5×–1.5×
 *    - Keyboard: A=set A, B=set B, L=toggle loop, Space=play/pause
 *
 * Bunny serves these as `video/x-m4v`. We add `<source type="video/mp4">` first
 * so every browser plays them inline.
 */
/**
 * Bunny STREAM embed links (iframe.mediadelivery.net/embed/…) can't play in a
 * <video> tag — they need an iframe. Self-serve lessons paste whatever link
 * Bunny gave them, so accept both formats instead of silently failing.
 * Branching happens in this hook-free wrapper so the player component's hook
 * order stays stable regardless of URL format.
 */
export function BunnyVideo(props: BunnyVideoProps) {
  // A lesson with no video URL used to render a dead <video src=""> — an empty
  // black player the student could click forever. Say what's actually going on
  // instead, and leave the rest of the lesson (which is fully built) usable.
  if (!props.src?.trim()) {
    return (
      <div
        className="relative flex w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-2xl border border-dashed border-nsm-border bg-nsm-surface/60 text-center"
        style={{ aspectRatio: "16 / 9" }}
      >
        <div
          className="text-[11px] font-bold uppercase tracking-[0.22em]"
          style={{ color: props.accentColor ?? "#f59e0b" }}
        >
          Video coming soon
        </div>
        <p className="max-w-sm px-6 text-[13px] leading-relaxed text-nsm-dim">
          This lesson's video hasn't been published yet. Everything below — the
          notes, the drills and the interactive practice tools — is ready to use.
        </p>
      </div>
    );
  }

  const isEmbedUrl = /iframe\.mediadelivery\.net|\/embed\//.test(props.src);
  if (isEmbedUrl) {
    return (
      <div
        className="relative w-full overflow-hidden rounded-2xl border border-nsm-border bg-black shadow-[0_8px_30px_rgba(0,0,0,0.45)]"
        style={{ aspectRatio: "16 / 9" }}
      >
        <iframe
          src={props.src}
          title={props.title}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return <BunnyVideoPlayer {...props} />;
}

function BunnyVideoPlayer({ src, poster, title, accentColor = "#f59e0b", lessonOrder }: BunnyVideoProps) {
  const [posterFailed, setPosterFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  // Pre-flight HEAD probe to detect 404 thumbnails so we can render the fallback
  useEffect(() => {
    if (!poster) return;
    setPosterFailed(false);
    const img = new Image();
    img.onload = () => setPosterFailed(false);
    img.onerror = () => setPosterFailed(true);
    img.src = poster;
  }, [poster]);

  const showPoster = poster && !posterFailed ? poster : undefined;
  const slotRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [floating, setFloating] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [aPoint, setAPoint] = useState<number | null>(null);
  const [bPoint, setBPoint] = useState<number | null>(null);
  const [loopOn, setLoopOn] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [duration, setDuration] = useState(0);
  const [now, setNow] = useState(0);

  // Detect mobile at mount — floating is disabled on narrow screens.
  // We capture this once; resizing mid-session is an edge case we ignore.
  const isMobileRef = useRef(
    typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );

  // ── Floating dock trigger ──────────────────────────────────────────────────
  // Watch the inline slot with IntersectionObserver. When it leaves the viewport
  // we flip `floating` → true. The <video> element is NOT remounted — its CSS
  // class changes so it visually moves to position:fixed in the corner.
  // On mobile we never set floating=true; video stays in the normal flow.
  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (isMobileRef.current) return; // no floating on mobile
        setFloating(!entry.isIntersecting);
      },
      { threshold: 0.05, rootMargin: "0px 0px -40% 0px" }
    );
    obs.observe(slot);
    return () => obs.disconnect();
  }, []);

  // Apply playbackRate when speed changes
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed]);

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v) return;
    setNow(v.currentTime);
    if (loopOn && aPoint !== null && bPoint !== null && v.currentTime >= bPoint) {
      v.currentTime = aPoint;
    }
  };

  const setA = () => videoRef.current && setAPoint(videoRef.current.currentTime);
  const setB = () => videoRef.current && setBPoint(videoRef.current.currentTime);
  const clearAB = () => {
    setAPoint(null);
    setBPoint(null);
    setLoopOn(false);
  };
  const jumpA = () => {
    if (videoRef.current && aPoint !== null) videoRef.current.currentTime = aPoint;
  };

  /** "Loop the last N seconds": A = now-N, B = now, loop ON. */
  const loopLast = (seconds: number) => {
    const v = videoRef.current;
    if (!v) return;
    const b = v.currentTime;
    const a = Math.max(0, b - seconds);
    setAPoint(a);
    setBPoint(b);
    setLoopOn(true);
    v.currentTime = a;
    v.play().catch(() => {});
  };

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => {});
    else v.pause();
  };

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === " ") { e.preventDefault(); togglePlay(); }
      else if (e.key === "a" || e.key === "A") setA();
      else if (e.key === "b" || e.key === "B") setB();
      else if (e.key === "l" || e.key === "L") setLoopOn((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aPoint, bPoint]);

  const fmt = (s: number) => {
    if (!isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  // ── Video CSS ──────────────────────────────────────────────────────────────
  // The <video> is always rendered inside the inline slot at the same position
  // in the React tree. We only change its CSS class — no unmount/remount.
  //
  // Inline:  position:absolute, fills the slot container.
  // Floating: position:fixed, snaps to viewport bottom-right corner.
  //           position:fixed escapes overflow:hidden on ancestors, so the
  //           slot container still shows its placeholder text correctly.
  //           BunnyVideo lives above the Framer Motion <motion.div> in
  //           LessonShell, so there is no ancestor CSS transform that would
  //           re-anchor a fixed-position child.
  const videoClassName = floating
    ? [
        "fixed bottom-4 right-4 z-[49]",
        "w-[min(360px,calc(100vw-2rem))]",
        "aspect-video rounded-2xl bg-black",
        "border border-amber-500/40 shadow-2xl ring-1 ring-amber-500/30",
      ].join(" ")
    : "absolute inset-0 h-full w-full bg-black";

  // ── Floating header overlay ────────────────────────────────────────────────
  // A thin control strip that sits directly above the fixed video in the corner.
  // It shows the loop range (if active) and an "⇈ Inline" button.
  // Width matches the video. Bottom offset = 1rem (video bottom) + video height.
  // At 360px wide (16:9): height = 360 × 9/16 = 202.5 px → bottom ≈ 218 px.
  const overlayBottomPx = 218; // 202.5px video + 16px (1rem) bottom gap, rounded

  const playVideo = () => videoRef.current?.play().catch(() => {});

  const FallbackPoster = () => (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center cursor-pointer"
      style={{
        background: `radial-gradient(ellipse at center, ${accentColor}40, ${accentColor}10 60%, #0a0a0a 100%)`,
      }}
      onClick={playVideo}
    >
      {lessonOrder != null && (
        <div
          className="text-[120px] font-bold leading-none"
          style={{ color: `${accentColor}90`, fontFamily: '"Cormorant Garamond", Georgia, serif' }}
        >
          {String(lessonOrder).padStart(2, "0")}
        </div>
      )}
      <div className="mt-2 max-w-[80%] px-4 text-base font-medium text-white/85" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
        {title}
      </div>
      {!videoFailed && (
        <div className="mt-5 flex h-14 w-14 items-center justify-center rounded-full bg-white/90 text-zinc-950 shadow-2xl transition hover:scale-110">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
        </div>
      )}
      {videoFailed && (
        <div className="mt-3 rounded-full border border-rose-400/40 bg-rose-500/10 px-3 py-1 text-[11px] uppercase tracking-wider text-rose-200">
          Video temporarily unavailable
        </div>
      )}
    </div>
  );

  // ── Expand (desktop only) ─────────────────────────────────────────────────
  // Breaks the player out of the max-w-5xl container so it fills more of the
  // screen on wide monitors. Uses the CSS full-bleed trick: position:relative +
  // left offsets the element so its centre aligns with the page centre at any
  // viewport width. overflow-x:clip on the wrapper prevents a horizontal bar.
  // Only available when not floating (floating already has its own sizing).
  const expandedWrapperStyle: React.CSSProperties =
    expanded && !isMobileRef.current && !floating
      ? {
          position: "relative",
          width: "min(92vw, 1500px)",
          left: "calc(50% - min(46vw, 750px))",
          overflowX: "clip",
        }
      : {};

  return (
    <div className="space-y-2" style={expandedWrapperStyle}>
      {/* ── Inline slot ───────────────────────────────────────────────────── */}
      {/* The <video> element lives here permanently. When floating=true, the   */}
      {/* video's CSS switches to position:fixed so it visually moves to the   */}
      {/* corner — but it stays at the same place in the React/DOM tree.       */}
      <div
        ref={slotRef}
        className="relative w-full overflow-hidden rounded-2xl border border-nsm-border bg-black shadow-2xl"
        style={{ aspectRatio: "16/9" }}
      >
        {/* Expand / collapse toggle — desktop only, hidden while floating */}
        {!isMobileRef.current && !floating && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="absolute top-2 right-2 z-20 flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-[11px] font-medium text-white/80 backdrop-blur-sm transition hover:bg-black/80 hover:text-white"
            title={expanded ? "Collapse video" : "Expand video"}
          >
            {expanded ? (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v3a2 2 0 01-2 2H3"/><path d="M21 8h-3a2 2 0 01-2-2V3"/><path d="M3 16h3a2 2 0 012 2v3"/><path d="M16 21v-3a2 2 0 012-2h3"/></svg>
                Collapse
              </>
            ) : (
              <>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 00-2 2v3"/><path d="M21 8V5a2 2 0 00-2-2h-3"/><path d="M3 16v3a2 2 0 002 2h3"/><path d="M16 21h3a2 2 0 002-2v-3"/></svg>
                Expand
              </>
            )}
          </button>
        )}
        {/* THE VIDEO — always mounted, never conditionally rendered */}
        <video
          ref={videoRef}
          poster={showPoster}
          controls
          preload="metadata"
          playsInline
          title={title}
          className={videoClassName}
          onPlay={() => setHasPlayed(true)}
          onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
          onTimeUpdate={onTimeUpdate}
          onError={() => setVideoFailed(true)}
        >
          <source src={src} type="video/mp4" />
          <source src={src} type="video/x-m4v" />
          <a href={src} target="_blank" rel="noreferrer" className="text-amber-300">{title}</a>
        </video>

        {/* Fallback poster — shown before first play when no real poster */}
        {!hasPlayed && !floating && (!poster || posterFailed) && <FallbackPoster />}

        {/* Clickable play overlay on top of the poster image.
            Clicking anywhere on the poster (or the centre play button) starts
            playback. No pointer-events-none — the whole area is the target. */}
        {!hasPlayed && showPoster && !floating && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/40 to-transparent cursor-pointer"
            onClick={playVideo}
            aria-label="Play video"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 text-zinc-950 shadow-2xl transition hover:scale-110">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
        )}

        {/* Placeholder shown in the inline slot while the video is docked in the corner */}
        {floating && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-zinc-950 text-center">
            {showPoster && <img src={showPoster} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />}
            <div className="relative z-10 text-sm text-nsm-dim">● Video docked in the corner — keep scrolling</div>
            <button
              onClick={() => slotRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="nsm-btn-ghost relative z-10"
            >
              Bring video back inline
            </button>
          </div>
        )}
      </div>

      {/* ── Floating header overlay ────────────────────────────────────────── */}
      {/* Sits directly above the fixed video in the corner. Contains the loop  */}
      {/* status indicator and the "⇈ Inline" bring-back button.               */}
      {floating && (
        <div
          className="fixed right-4 z-50 flex w-[min(360px,calc(100vw-2rem))] items-center justify-between rounded-t-2xl border-x border-t border-amber-500/40 bg-nsm-bg/90 px-3 py-1.5 text-[11px] uppercase tracking-wider backdrop-blur-sm"
          style={{ bottom: `${overlayBottomPx}px` }}
        >
          <span className="font-semibold text-amber-300">● Lesson video</span>
          <div className="flex items-center gap-2">
            {loopOn && aPoint !== null && bPoint !== null && (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-300">
                ⤺ {fmt(aPoint)}–{fmt(bPoint)}
              </span>
            )}
            <button
              onClick={() => slotRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className="text-nsm-dim hover:text-nsm-ink"
              title="Bring video back inline"
            >
              ⇈ Inline
            </button>
          </div>
        </div>
      )}

      {/* ── Practice bar ──────────────────────────────────────────────────── */}
      {/* Always visible — controls work regardless of floating state.         */}
      <div className="rounded-xl border border-nsm-border bg-nsm-card/70">
        {/* One-click loop presets */}
        <div className="flex flex-wrap items-center gap-2 border-b border-nsm-border px-3 py-2 text-xs">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-nsm-dim">Loop the last</span>
          {[5, 10, 30].map((n) => (
            <button
              key={n}
              onClick={() => loopLast(n)}
              className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-amber-200 transition hover:bg-amber-500/20"
              title={`Loop the last ${n} seconds — click again to update`}
            >
              {n}s
            </button>
          ))}
          {loopOn && (
            <button onClick={() => setLoopOn(false)} className="ml-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-rose-200 hover:bg-rose-500/20">
              Stop loop
            </button>
          )}
          <span className="ml-auto text-[10px] text-nsm-dim">
            shortcuts: <kbd className="rounded bg-nsm-surface px-1">space</kbd> ▶ · <kbd className="rounded bg-nsm-surface px-1">A</kbd> · <kbd className="rounded bg-nsm-surface px-1">B</kbd> · <kbd className="rounded bg-nsm-surface px-1">L</kbd> loop
          </span>
        </div>

        {/* Manual A/B + speed */}
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs">
          <div className="flex items-center gap-1.5">
            <button onClick={setA} className="rounded-md border border-nsm-border bg-nsm-surface px-2 py-1 text-nsm-ink hover:bg-nsm-card" title="Mark current time as A">
              Set A {aPoint !== null && <span className="ml-1 text-emerald-300">{fmt(aPoint)}</span>}
            </button>
            <button onClick={setB} className="rounded-md border border-nsm-border bg-nsm-surface px-2 py-1 text-nsm-ink hover:bg-nsm-card" title="Mark current time as B">
              Set B {bPoint !== null && <span className="ml-1 text-rose-300">{fmt(bPoint)}</span>}
            </button>
            <button
              onClick={() => setLoopOn((v) => !v)}
              disabled={aPoint === null || bPoint === null || (bPoint ?? 0) <= (aPoint ?? 0)}
              className={`rounded-md border px-2 py-1 transition disabled:opacity-30 ${
                loopOn ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-nsm-border bg-nsm-surface text-nsm-ink hover:bg-nsm-card"
              }`}
            >
              ⤺ Loop {loopOn ? "ON" : "OFF"}
            </button>
            <button onClick={jumpA} disabled={aPoint === null} className="rounded-md border border-nsm-border bg-nsm-surface px-2 py-1 text-nsm-ink hover:bg-nsm-card disabled:opacity-30">
              ⇤ A
            </button>
            <button onClick={clearAB} className="rounded-md border border-nsm-border bg-nsm-surface px-2 py-1 text-nsm-dim hover:text-nsm-ink" title="Clear A/B">
              ✕
            </button>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <span className="mr-1 text-[10px] uppercase tracking-wider text-nsm-dim">Speed</span>
            {[0.5, 0.75, 1, 1.25, 1.5].map((s) => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                className={`rounded-md border px-1.5 py-1 text-[11px] ${
                  speed === s ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-nsm-border bg-nsm-surface text-nsm-dim"
                }`}
              >
                {s}×
              </button>
            ))}
          </div>
        </div>

        {duration > 0 && (
          <div className="relative mx-3 mb-2 h-1.5 overflow-hidden rounded-full bg-nsm-surface">
            {aPoint !== null && bPoint !== null && (
              <div
                className="absolute h-full bg-amber-500/60"
                style={{ left: `${(aPoint / duration) * 100}%`, width: `${((bPoint - aPoint) / duration) * 100}%` }}
              />
            )}
            <div className="absolute h-full w-0.5 bg-amber-300" style={{ left: `${(now / duration) * 100}%` }} />
            {aPoint !== null && <div className="absolute top-0 h-full w-0.5 bg-emerald-400" style={{ left: `${(aPoint / duration) * 100}%` }} />}
            {bPoint !== null && <div className="absolute top-0 h-full w-0.5 bg-rose-400" style={{ left: `${(bPoint / duration) * 100}%` }} />}
          </div>
        )}
      </div>
    </div>
  );
}
